import { Button, Grid } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import TTReportItem from '../../../models/payload/TTReportItem';

const useStyles = makeStyles()(() => ({
  card: { backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: '10px 16px', margin: '0 20px 12px 20px', },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', },
  title: { fontSize: '1.05rem', fontWeight: 700, color: '#000', },
  sub: { fontSize: '.85em', color: '#777', marginLeft: 10, fontWeight: 'normal', },
  colTitle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8em', fontWeight: 600, letterSpacing: '.05em', color: '#444', margin: '6px 0 2px 0', },
  row: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderTop: '1px solid #e8e8e8', padding: '3px 0', fontSize: '.85rem', },
  id: { color: '#777', marginRight: 8, },
  name: { color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', },
  up: { color: '#1b6e2f', fontWeight: 'bold', whiteSpace: 'nowrap', },
  down: { color: '#b3261e', fontWeight: 'bold', whiteSpace: 'nowrap', },
  pct: { color: '#666', fontSize: '.85em', marginLeft: 6, fontWeight: 'normal', },
  empty: { color: '#999', fontSize: '.9em', padding: '5px 0', },
}));

// Whole kroner with a space as thousands separator, like the table.
const amountFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });

// "+4,2 %" / "-26,1 %" — one decimal, comma, sign. Empty when last year is 0 (no base to compare).
export const formatChangePercent = (nettA: number, nettB: number) => {
  if (!nettB) return '';
  const pct = ((nettA - nettB) / Math.abs(nettB)) * 100;
  return (pct > 0 ? '+' : '') + pct.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace('−', '-') + ' %';
};

interface Props {
  rows: TTReportItem[];
  showId: boolean;
}

// "Største endringer i kroner": the three rows (varegruppe, kunde, ...) with the largest growth and
// the three with the largest fall in kroner, for the selected utplukk, against the same period last year.
// Calculated from the rows the report already returned - no extra API call.
const StorsteEndringer = ({ rows, showId }: Props) => {
  const { classes } = useStyles();
  const [open, setOpen] = useState(true);
  const diff = (r: TTReportItem) => (r.nettA || 0) - (r.nettB || 0);

  const growth = rows.filter((r) => diff(r) > 0).sort((a, b) => diff(b) - diff(a)).slice(0, 3);
  const fall = rows.filter((r) => diff(r) < 0).sort((a, b) => diff(a) - diff(b)).slice(0, 3);

  const renderList = (list: TTReportItem[], up: boolean) => list.length === 0
    ? <div className={classes.empty}>Ingen</div>
    : list.map((r) => (
      <div className={classes.row} key={r.id || r.name}>
        <span className={classes.name}>{showId ? <span className={classes.id}>{r.id}</span> : null}{r.name}</span>
        <span className={up ? classes.up : classes.down}>
          {(up ? '+' : '') + amountFormat.format(Math.round(diff(r)))}
          <span className={classes.pct}>{formatChangePercent(r.nettA, r.nettB)}</span>
        </span>
      </div>
    ));

  return (
    <div className={classes.card}>
      <div className={classes.head}>
        <div className={classes.title}>Største endringer i kroner<span className={classes.sub}>mot samme periode i fjor</span></div>
        <Button size="small" variant="outlined" color="inherit" onClick={() => setOpen(!open)}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ textTransform: 'none', height: 28, fontSize: '0.8rem' }}>
          {open ? 'Skjul' : 'Vis'}
        </Button>
      </div>
      {open ? (
        <Grid container columnSpacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <div className={classes.colTitle}><TrendingUpIcon fontSize="small" />STØRST VEKST</div>
            {renderList(growth, true)}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <div className={classes.colTitle}><TrendingDownIcon fontSize="small" />STØRST FALL</div>
            {renderList(fall, false)}
          </Grid>
        </Grid>
      ) : null}
    </div>
  );
};

export default StorsteEndringer;
